import 'firebase/compat/database'

import * as CryptoJS from 'crypto-js'

import firebase from 'firebase/compat/app'
import getCollectionWithAlt from './getCollectionWithAlt'
import moment from 'moment-timezone'

export interface ICampaignDetails {
  useCase?: string
  useCaseDescription?: string
  sampleMessages?: string[]
  messageIncludesEmbeddedLink?: boolean
  messageIncludesPhoneNumbers?: boolean
  endUserConsent?: string
  optInKeywords?:string
  optInMessage?:string
}

export enum MIGRATION_STATUS {
  COMPLETED = 'completed',
  PENDING = 'pending',
  QUEUED = 'queued',
  REVERTED = 'reverted'
}

const NUMBER_TYPES = ["local" , "mobile" , "tollFree"] as const;
type NumberTypes = typeof NUMBER_TYPES[number];

type NumberMeta = { [number: string]: { numberType: string; countryCode: string } }

export default class TwilioAccount {
  private _id: string
  private _data: firebase.firestore.DocumentData
  private _ref: firebase.firestore.DocumentReference

  constructor(snapshot?: firebase.firestore.DocumentSnapshot) {
    if (snapshot) {
      this._id = snapshot.id
      this._ref = snapshot.ref
      this._data = snapshot.data()
    } else {
      this._ref = TwilioAccount.collectionRef().doc()
      this._data = {}
      this._id = this._ref.id
      this._data.numbers = {}
      this._data.forwarding_numbers = {}
      this._data.whisper_msg = {}
      this._data.play_warning = {}
      this._data.call_recording = {}
      this._data.number_pool_numbers_is_active = {}
      this._data.number_name = {}
      this._data.numberMeta = {};
      this.dateAdded = moment()
    }
  }

  public static collectionRef(): firebase.firestore.CollectionReference {
    return firebase.firestore().collection('twilio_accounts')
  }

  public save(): Promise<TwilioAccount> {
    const _self = this
    return new Promise((resolve,reject) => {
      _self.dateUpdated = moment()
      _self._ref.set(_self._data).then(() => {
        resolve(_self)
      }).catch((error) => {
        reject(error);
      });
    });
  }

  public static getById(id) {
    return new Promise((resolve, reject) => {
      TwilioAccount.collectionRef()
        .doc(id)
        .get()
        .then(snapshot => {
          resolve(new TwilioAccount(snapshot))
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }

  public static fetchByLocationId(locationId: string) {
    return TwilioAccount.collectionRef()
      .where('location_id', '==', locationId)
      .limit(1)
  }

  static getByLocation(locationId: string): Promise<TwilioAccount> {
    return new Promise((resolve, reject) => {
      const collectionToUse = getCollectionWithAlt(TwilioAccount);
      collectionToUse
        .where('location_id', '==', locationId)
        .get()
        .then(snapshot => {
          if (snapshot.empty) {
            return resolve()
          }
          return resolve(new TwilioAccount(snapshot.docs[0]))
        })
        .catch(() => {
          return resolve(null)
        })
    })
  }

  public static getByCompanyId(companyId: string): Promise<TwilioAccount> {
    return new Promise((resolve, reject) => {
      TwilioAccount.collectionRef()
        .where('company_id', '==', companyId)
        .get()
        .then(snapshot => {
          if (!snapshot.empty) {
            resolve(new TwilioAccount(snapshot.docs[0]))
          } else {
            resolve()
          }
        })
        .catch(err => {
          console.error(err)
          reject(err.message)
        })
    })
  }

  public static getByLocationId(locationId: string): Promise<TwilioAccount> {
    return new Promise((resolve, reject) => {
      TwilioAccount.collectionRef()
        .where('location_id', '==', locationId)
        .get()
        .then(snapshot => {
          if (!snapshot.empty) {
            resolve(new TwilioAccount(snapshot.docs[0]))
          } else {
            resolve()
          }
        })
        .catch(err => {
          console.error(err)
          reject(err.message)
        })
    })
  }

  static getByNumber(number): Promise<TwilioAccount> {
    return new Promise((resolve, reject) => {
      TwilioAccount.collectionRef()
        .where('numbers.' + number, '>', '')
        .get()
        .then(snapshot => {
          if (snapshot.empty) {
            return resolve()
          }
          return resolve(new TwilioAccount(snapshot.docs[0]))
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }

  static getByAccountSid(accountSid): Promise<TwilioAccount> {
    return new Promise<TwilioAccount>((resolve, reject) => {
      TwilioAccount.collectionRef()
        .where('account_sid', '==', accountSid)
        .get()
        .then(snapshot => {
          if (snapshot.empty) {
            return reject('No matching twilio account found for ' + accountSid)
          }

          return resolve(new TwilioAccount(snapshot.docs[0]))
        })
        .catch(err => {
          console.error(err)
          return reject(err)
        })
    })
  }

  get id(): string {
    return this._id
  }

  get ref() {
    return this._ref
  }

  get data(): firebase.firestore.DocumentData {
    return this._data
  }

  get locationId(): string {
    return this._data.location_id
  }

  set locationId(locationId: string) {
    this._data.location_id = locationId
  }

  get companyId(): string {
    return this._data.company_id
  }

  set companyId(company_id: string) {
    this._data.company_id = company_id
  }

  get dateAdded(): moment.Moment {
    return moment(this._data.date_added)
  }

  set dateAdded(dateAdded: moment.Moment) {
    this._data.date_added = dateAdded.toDate()
  }

  get dateUpdated(): moment.Moment {
    return moment(this._data.date_updated)
  }

  set dateUpdated(dateUpdated: moment.Moment) {
    this._data.date_updated = dateUpdated.toDate()
  }

  get accountSID(): string {
    return this._data.account_sid
  }

  set accountSID(accountSID: string) {
    this._data.account_sid = accountSID
  }

  get token(): string {
    if (this._data.token)
      return CryptoJS.AES.decrypt(this._data.token, 'apples&oranges').toString(
        CryptoJS.enc.Utf8
      )
  }

  set token(token: string) {
    this._data.token = CryptoJS.AES.encrypt(token, 'apples&oranges').toString()
  }

  get numbers(): { [field: string]: string } {
    return this._data.numbers
  }

  get forwardingNumbers(): { [field: string]: string } {
    if (!this._data.forwarding_numbers) this._data.forwarding_numbers = {}
    return this._data.forwarding_numbers
  }

  set forwardingNumbers(values) {
    this._data.forwarding_numbers = values
  }

  get assignedToNumber(): { [field: string]: string } {
    if (!this._data.assigned_to_numbers) this._data.assigned_to_numbers = {}
    return this._data.assigned_to_numbers
  }

  set assignedToNumber(values) {
    this._data.assigned_to_numbers = values
  }

  get defaultOutboundNumber(): string {
    return this._data.default_outbound_number
  }

  set defaultOutboundNumber(defaultOutboundNumber: string) {
    this._data.default_outbound_number = defaultOutboundNumber
  }

  get callRecording(): { [field: string]: string } {
    if (!this._data.call_recording) this._data.call_recording = {}

    return this._data.call_recording
  }

  get numberPoolNumbersIsActive(): { [field: string]: boolean } {
    if (!this._data.number_pool_numbers_is_active)
      this._data.number_pool_numbers_is_active = {}
    return this._data.number_pool_numbers_is_active
  }

  set callRecording(values) {
    this._data.call_recording = values
  }

  get playWarningMessage(): { [field: string]: string } {
    if (!this._data.play_warning) this._data.play_warning = {}
    return this._data.play_warning
  }

  set playWarningMessage(values) {
    this._data.play_warning = values
  }

  get twimlAppId(): string {
    return this._data.twiml_app_id
  }

  set twimlAppId(twimlAppId: string) {
    this._data.twiml_app_id = twimlAppId
  }

  get apiKey(): string {
    return this._data.api_key
  }

  set apiKey(apiKey: string) {
    this._data.api_key = apiKey
  }

  get apiSecret(): string {
    return this._data.api_secret
  }

  set apiSecret(apiSecret: string) {
    this._data.api_secret = apiSecret
  }

  get whisper(): { [field: string]: string } {
    if (!this._data.whisper) this._data.whisper = {}
    return this._data.whisper
  }

  set whisper(values) {
    this._data.whisper = values
  }

  get whisperMessages(): { [field: string]: string } {
    if (!this._data.whisper_msg) this._data.whisper_msg = {}
    return this._data.whisper_msg
  }

  set whisperMessages(values) {
    this._data.whisper_msg = values
  }

  get incomingWhisper(): { [field: string]: boolean } {
    if (!this._data.incoming_whisper) this._data.incoming_whisper = {}
    return this._data.incoming_whisper
  }

  set incomingWhisper(values) {
    this._data.incoming_whisper = values
  }

  get incomingWhisperMessages(): { [field: string]: string } {
    if (!this._data.incoming_whisper_msg) this._data.incoming_whisper_msg = {}
    return this._data.incoming_whisper_msg
  }

  set incomingWhisperMessages(values) {
    this._data.incoming_whisper_msg = values
  }

  get enableCallerId(): { [field: string]: boolean } {
    if (!this._data.enable_callerid) this._data.enable_callerid = {}
    return this._data.enable_callerid
  }

  set enableCallerId(values) {
    this._data.enable_callerid = values
  }

  get voicemail(): { [field: string]: string } {
    if (!this._data.voicemail) this._data.voicemail = {}
    return this._data.voicemail
  }

  set voicemail(values) {
    this._data.voicemail = values
  }

  get voicemailTimeout(): { [field: string]: number } {
    if (!this._data.voicemail_timeout) this._data.voicemail_timeout = {}
    return this._data.voicemail_timeout
  }

  set voicemailTimeout(values) {
    this._data.voicemail_timeout = values
  }

  get voicemailOutbound(): { [field: string]: string } {
    if (!this._data.voicemail_outbound) this._data.voicemail_outbound = {}
    return this._data.voicemail_outbound
  }

  set voicemailOutbound(values) {
    this._data.voicemail_outbound = values
  }

  get voicemailOutboundTimeout(): { [field: string]: number } {
    if (!this._data.voicemail_outbound_timeout)
      this._data.voicemail_outbound_timeout = {}
    return this._data.voicemail_outbound_timeout
  }

  set voicemailOutboundTimeout(values) {
    this._data.voicemail_outbound_timeout = values
  }

  get numberPools(): string[] {
    return this._data.number_pools || []
  }

  set numberPools(poolIds: string[]) {
    this._data.number_pools = poolIds
  }

  get hostedNumbers(): { [field: string]: boolean } {
    if (!this._data.hosted_numbers) this._data.hosted_numbers = {}
    return this._data.hosted_numbers
  }

  set hostedNumbers(values) {
    this._data.hosted_numbers = values
  }

  get numberName(): { [field: string]: string } {
    if (!this._data.number_name) this._data.number_name = {}
    return this._data.number_name
  }

  set numberName(values) {
    this._data.number_name = values
  }

  get numberMeta(): NumberMeta {
    if (!this._data.numberMeta) this._data.numberMeta = {};
    return this._data.numberMeta;
  }

  set numberMeta(values: NumberMeta) {
    const invalidNumberTypes = Object.entries(values)?.filter(([, meta]) => !NUMBER_TYPES.includes(meta.numberType as NumberTypes)) ?? [];

    if(invalidNumberTypes.length > 0) {
      throw new Error(`${invalidNumberTypes.join(", ")} has invalid Number Type. Number Type should be one of ${NUMBER_TYPES.join(", ")}.`);
    }
    this._data.numberMeta = values;
  }

  get validateSms(): boolean {
    return this._data.validate_sms
  }
  set validateSms(validateSms: boolean) {
    this._data.validate_sms = validateSms
  }

  get createSubAccountsAutomatically(): boolean {
    return typeof this._data.auto_create_subacc === 'boolean'
      ? this._data.auto_create_subacc
      : true // When the field isn't there, it will fallback to true
  }

  set createSubAccountsAutomatically(createSubAccountsAutomatically: boolean) {
    this._data.auto_create_subacc = createSubAccountsAutomatically
  }

  get underGHLAccount(): boolean {
    return this._data.under_ghl_account || false
  }

  set underGHLAccount(underGHL: boolean) {
    this._data.under_ghl_account = underGHL
  }

  get migrationNumbers(): string[] {
    return this._data.migration_numbers || []
  }

  set migrationNumbers(values: string[]) {
    this._data.migration_numbers = values
  }

  get phoneNumberCallerIdsMapping() {
    if (!this._data.phone_number_caller_ids_mapping)
      this._data.phone_number_caller_ids_mapping = {}
    return this._data.phone_number_caller_ids_mapping
  }

  set phoneNumberCallerIdsMapping(values) {
    this._data.phone_number_caller_ids_mapping = values
  }

  get compliance(): { [field: string]: any } {
    return this._data.compliance || {}
  }

  set compliance(values: { [field: string]: any }) {
    this._data.compliance = values
  }

  get settings(): { [field: string]: any } {
    return this._data.settings || {}
  }

  set settings(values: { [field: string]: any }) {
    this._data.settings = values
  }

  get campaignDetails(): ICampaignDetails {
    if (!this._data.campaignDetails) this.data.campaignDetails = {}
    if (!this._data.campaignDetails.useCase)
      this.data.campaignDetails.useCase = ''
    if (!this._data.campaignDetails.useCaseDescription)
      this.data.campaignDetails.useCaseDescription = ''
    if (
      !this._data.campaignDetails.sampleMessages ||
      this._data.campaignDetails.sampleMessages?.length === 0
    )
      this.data.campaignDetails.sampleMessages = ['', '']
    this.data.campaignDetails.messageIncludesEmbeddedLink =
      this._data.campaignDetails.messageIncludesEmbeddedLink ?? true
    this.data.campaignDetails.messageIncludesPhoneNumbers =
      this._data.campaignDetails.messageIncludesPhoneNumbers ?? true
    if (!this._data.campaignDetails.endUserConsent)
      this.data.campaignDetails.endUserConsent = ''
    if (!this._data.campaignDetails.optInKeywords)
      this.data.campaignDetails.optInKeywords = ''
    if (!this._data.campaignDetails.optInMessage)
      this.data.campaignDetails.optInMessage = ''
    return this._data.campaignDetails
  }

  set campaignDetails(payload: ICampaignDetails) {
    if (!this.data.campaignDetails) {
      this._data.campaignDetails = {}
    }
    if (payload.useCase) {
      this._data.campaignDetails.useCase = payload.useCase
    }
    if (payload.useCaseDescription) {
      this._data.campaignDetails.useCaseDescription = payload.useCaseDescription
    }
    if (payload.sampleMessages) {
      this._data.campaignDetails.sampleMessages = payload.sampleMessages
    }
    if (payload.messageIncludesEmbeddedLink) {
      this._data.campaignDetails.messageIncludesEmbeddedLink =
        payload.messageIncludesEmbeddedLink
    }
    if (payload.messageIncludesPhoneNumbers) {
      this._data.campaignDetails.messageIncludesPhoneNumbers =
        payload.messageIncludesPhoneNumbers
    }
    if (payload.endUserConsent) {
      this._data.campaignDetails.endUserConsent = payload.endUserConsent
    }
    if (payload.optInKeywords) {
      this._data.campaignDetails.optInKeywords = payload.optInKeywords
    }
    if (payload.optInMessage) {
      this._data.campaignDetails.optInKeywords = payload.optInMessage
    }
  }

  get starterBrandInfo(): { [field: string]: string } {
    return this._data.starterBrandInfo || {}
  }

  set starterBrandInfo(values: { [field: string]: string }) {
    this._data.starterBrandInfo = values
  }

  get incomingCallBlocked(): boolean {
    return this._data.incomingCallBlocked || false
  }

  set incomingCallBlocked(incomingCallBlocked: boolean) {
    this._data.incomingCallBlocked = incomingCallBlocked
  }
}
